import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  Paper,
  Typography,
  Button,
  Box,
  Chip,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  CircularProgress,
  alpha,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { Property } from '../../types/property.types';

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: 'right' | 'left' | 'center';
}

const columns: Column[] = [
  { id: 'name', label: 'Site Name', minWidth: 170 },
  { id: 'address.line1', label: 'Address', minWidth: 170 },
  { id: 'address.city', label: 'Town/City', minWidth: 100 },
  { id: 'address.county', label: 'County', minWidth: 100 },
  { id: 'address.postcode', label: 'Postcode', minWidth: 100 },
  { id: 'status', label: 'Status', minWidth: 100 },
];

const getPropertyValue = (property: Property, path: string): string => {
  const parts = path.split('.');
  let value: any = property;
  
  for (const part of parts) {
    value = value?.[part];
  }
  
  return value || 'N/A';
};

const renderCellContent = (property: Property, columnId: string): string => {
  if (columnId === 'address.line1') {
    const parts = [
      property.address?.line1,
      property.address?.line2,
    ].filter(Boolean);
    return parts.join(', ') || 'N/A';
  }

  return getPropertyValue(property, columnId);
};

interface PropertyListProps {
  properties: Property[];
  onEdit?: (property: Property) => void;
  onDelete?: (propertyId: string) => void;
  onSelect?: (property: Property) => void;
  clientId?: string;
  showClientName?: boolean;
}

const formatAddress = (property: Property) => {
  const parts = [
    property.address.line1,
    property.address.line2,
    property.address.city,
    property.address.county,
    property.address.postcode,
  ].filter(Boolean);
  return parts.join(', ');
};

const PropertyList: React.FC<PropertyListProps> = ({
  properties,
  onEdit,
  onDelete,
  onSelect,
  clientId,
  showClientName = true,
}) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState('name');

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handlePropertyClick = (property: Property) => {
    if (onSelect) {
      onSelect(property);
    } else {
      // Use either the provided clientId or get it from the property's client field
      // property.client can be either a string (ID) or an object with _id
      const propertyClientId = clientId || 
        (typeof property.client === 'string' ? property.client : property.client?._id);
      
      if (!propertyClientId) {
        
        return;
      }
      navigate(`/utilities/clients/${propertyClientId}/properties/${property._id}`);
    }
  };

  // Sort and paginate properties
  const sortedProperties = useMemo(() => {
    return [...properties].sort((a, b) => {
      const aValue = getPropertyValue(a, orderBy);
      const bValue = getPropertyValue(b, orderBy);
      
      if (order === 'asc') {
        return aValue.localeCompare(bValue);
      }
      return bValue.localeCompare(aValue);
    });
  }, [properties, order, orderBy]);

  const paginatedProperties = sortedProperties.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  if (!properties.length) {
    return (
      <Paper 
        elevation={3} 
        sx={{ 
          p: 3, 
          textAlign: 'center',
          backgroundColor: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>No properties found.</Typography>
      </Paper>
    );
  }

  return (
    <Paper 
      elevation={3} 
      sx={{ 
        width: '100%', 
        overflow: 'hidden',
        backgroundColor: 'rgba(15, 23, 42, 0.6)',
        backdropFilter: 'blur(10px)',
        borderRadius: 2,
        border: '1px solid rgba(255, 255, 255, 0.1)'
      }}
    >
      <TableContainer sx={{ maxHeight: 440 }}>
        <Table stickyHeader aria-label="sticky table" sx={{
          '& .MuiTableCell-root': {
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
          }
        }}>
          <EnhancedTableHead
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={properties.length}
            columns={columns}
            hasActions={!!(onEdit || onDelete)}
          />
          <TableBody>
            {paginatedProperties.map((property) => (
              <TableRow
                hover
                role="checkbox"
                tabIndex={-1}
                key={property._id}
                onClick={() => handlePropertyClick(property)}
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(59, 130, 246, 0.08)'
                  },
                  '&:last-child td, &:last-child th': { 
                    border: 0 
                  }
                }}
              >
                {columns.map((column) => (
                  <TableCell 
                    key={column.id} 
                    align={column.align}
                    sx={{ 
                      color: 'text.primary',
                      fontSize: '0.875rem'
                    }}
                  >
                    {renderCellContent(property, column.id)}
                  </TableCell>
                ))}
                {(onEdit || onDelete) && (
                  <TableCell align="right">
                    {onEdit && (
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(property);
                        }}
                        sx={{ 
                          color: 'primary.light',
                          '&:hover': { 
                            backgroundColor: alpha('#3b82f6', 0.1) 
                          } 
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                    {onDelete && (
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(property._id);
                        }}
                        sx={{ 
                          color: 'error.light',
                          '&:hover': { 
                            backgroundColor: alpha('#ef4444', 0.1) 
                          } 
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={properties.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          '.MuiTablePagination-selectIcon': { color: 'text.secondary' },
          '.MuiTablePagination-select': { color: 'text.primary' },
          '.MuiTablePagination-displayedRows': { color: 'text.secondary' },
        }}
      />
    </Paper>
  );
};

interface EnhancedTableProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
  order: 'asc' | 'desc';
  orderBy: string;
  rowCount: number;
  columns: Column[];
  hasActions?: boolean;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort, columns, hasActions } = props;
  const createSortHandler =
    (property: string) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {columns.map((column) => (
          <TableCell
            key={column.id}
            align={column.align}
            sortDirection={orderBy === column.id ? order : false}
            sx={{ 
              backgroundColor: 'rgba(15, 23, 42, 0.8)',
              fontWeight: 600,
              color: 'text.primary'
            }}
          >
            <TableSortLabel
              active={orderBy === column.id}
              direction={orderBy === column.id ? order : 'asc'}
              onClick={createSortHandler(column.id)}
              sx={{
                color: 'text.primary',
                '&.Mui-active': {
                  color: 'primary.main',
                },
                '& .MuiTableSortLabel-icon': {
                  color: 'primary.main !important',
                },
              }}
            >
              {column.label}
            </TableSortLabel>
          </TableCell>
        ))}
        {hasActions && (
          <TableCell 
            align="right"
            sx={{ 
              backgroundColor: 'rgba(15, 23, 42, 0.8)',
              fontWeight: 600,
              color: 'text.primary'
            }}
          >
            Actions
          </TableCell>
        )}
      </TableRow>
    </TableHead>
  );
}

export default PropertyList;
